import { getEquivalentSRS, getEPSGCode } from './CoordinatesUtils';

import {
    isString,
    isArray,
    isObject,
    head,
    castArray,
    sortBy
} from '@config/utils';
import get from 'lodash-es/get';

let WMTSUtils;

export const extractException = root => {
    const report = root["ows:ExceptionReport"] && {
        ...(get(root, 'ows:ExceptionReport.$') || {}),
        exception: get(root, 'ows:ExceptionReport.ows:Exception') // e.g. image/png
    };
    if(!report){
        return null;
    }else{
        return {
            report,
            text: get(report.exception, 'ows:ExceptionText'),
            code: get(report.exception, '$["exceptionCode"]'),
            locator: get(report.exception, '$["locator"]'),
        };
    }
};

export const extractCredits = attribution => {
    const title = attribution && attribution.Title;
    const logo = attribution.LogoURL && {
        ...(get(attribution, 'LogoURL.$') || {}),
        format: get(attribution, 'LogoURL.Format') // e.g. image/png
    };
    const link = get(attribution, 'OnlineResource.$["xlink:href"]');
    return {
        title,
        logo,
        imageUrl: get(attribution, 'LogoURL.OnlineResource.$["xlink:href"]'),
        link
    };
};

export const getDefaultMatrixId = (options) => {
    let matrixIds = new Array(30);
    for (let z = 0; z < 30; ++z) {
        // generate matrixIds arrays for this WMTS
        matrixIds[z] = options.tileMatrixPrefix + z;
    }
    return matrixIds;
};

export const getMatrixIds = (matrix, srs) => {
    return ((isObject(matrix) && isArray(matrix[srs]) && matrix[srs]) || (isArray(matrix) && matrix) || []);
};

export const limitMatrix = (matrix, len) => {
    if (matrix.length > len) {
        return matrix.slice(0, len);
    }
    if (matrix.length < len) {
        return matrix.concat(new Array(len - matrix.length));
    }
    return matrix;
};

export const getTileMatrixSet = (tileMatrixSet, srs, allowedSRS, matrixIds = {}, defaultMatrix = srs) => {
    if (tileMatrixSet && isString(tileMatrixSet)) {
        return tileMatrixSet;
    }
    if (tileMatrixSet) {
        return getEquivalentSRS(srs, allowedSRS).reduce((previous, current) => {
            if (isArray(tileMatrixSet)) {
                const matching = head(tileMatrixSet.filter((matrix) => ((matrix["ows:Identifier"] === current || getEPSGCode(matrix["ows:SupportedCRS"]) === current) && matrixIds[matrix["ows:Identifier"]])));
                return matching && matching["ows:Identifier"] ? matching["ows:Identifier"] : previous;
            } else if (isObject(tileMatrixSet)) {
                return tileMatrixSet[current] || previous;
            }
            return previous;
        }, defaultMatrix);
    }

    return defaultMatrix;
};

/**
 * Returns the first  available requestEncoding from the JSON
 */
export const getRequestEncoding = json => {
    const operations = WMTSUtils.getOperations(json);
    return WMTSUtils.getOperation(operations, "GetTile", "KVP") ? "KVP" : WMTSUtils.getOperation(operations, "GetTile", "RESTful") && "RESTful";
};

export const getOperations = (json = {}) => castArray(json.Capabilities["ows:OperationsMetadata"]["ows:Operation"]);

/**
 * gets the first operation of the type and with the name provided from the 'Operations' array of the WMTS Capabilities json parsed object
 */
export const getOperation = (operations, name, type) => {
    return head(
        castArray(head(
            operations
                .filter((operation) => operation.$.name === name)
                .map((operation) => castArray(operation["ows:DCP"]["ows:HTTP"]["ows:Get"]))
        ) || [])
            .filter((request) => (request["ows:Constraint"] && request["ows:Constraint"]["ows:AllowedValues"]["ows:Value"]) === type)
            .map((request) => request.$["xlink:href"])
    );
};

/**
 * Returns the GetTile Url from the record. This allows to get to get the proper
 * url template.
 * If requestEncoding is KVP uses GetTileURL, otherwise (RESTful) uses ResourceURL or GetTileURL
 */
export const getGetTileURL = ({ ResourceURL, GetTileURL, requestEncoding } = {}) => {
    if (requestEncoding === "KVP") {
        return GetTileURL;
    }
    return ResourceURL
        // TODO: support for multiple URLs
        && castArray(ResourceURL).map(({ $ = {} }) => $.template || $.value)
        || GetTileURL;
};

/**
 * Returns the the original capabilities. if not present, try returns the GetTileURL.
 * This is also a useful identifier for the source.
 */
export const getCapabilitiesURL = (record = {}) => {
    return head(castArray(record.capabilitiesURL || record.GetTileURL));
};

/**
 * Gets the default style for the WMTS Capabilities Layer entry
 * @param {object} layer the layer object of the WMTSCapabilities
 * @return {string} the identifier of the default style
 */

export const getDefaultStyleIdentifier = (layer) => {
    if (layer?.Style) {
        // if there's only one style, assume it's the default
        if (castArray(layer.Style).length === 1) {
            return head(
                castArray(layer.Style)
                    // the identifier content value is needed
                    .map(l => l["ows:Identifier"]));
        }

        return head(
            castArray(layer.Style)
                // default is identified by XML attribute isDefault
                .filter(({ $ = {} }) => $.isDefault === "true")
                // the identifier content value is needed
                .map(l => l["ows:Identifier"]));
    }
    return null;
};

/**
 * gets the first format available in the list
 */
export const getDefaultFormat = layer => head(castArray(layer.Format));

/**
 * This avoid https://openlayers.org/en/v5.3.0/doc/errors/#17 in case the scale tilematrix
 * is not naturally sorted by scale denominator.
 * for instance https://wxs.ign.fr/choisirgeoportail/geoportail/wmts
 */
export const sortTileMatrix = (tileMatrixSet, ids)  => {
    if (!tileMatrixSet) {
        return tileMatrixSet;
    }
    return {
        ...tileMatrixSet,
        TileMatrix:
            // sort by scale denominator, decendent
            (tileMatrixSet.TileMatrix ? tileMatrixSet.TileMatrix.map(t => ({ ...t, ScaleDenominator: Number(t.ScaleDenominator) })) : []).sort(sortBy("ScaleDenominator"))
                .reverse()
                // return only the Ids allowed to match array indexes
                .filter(t => ids ? ids.map(({ identifier } = {}) => identifier).indexOf(t["ows:Identifier"]) >= 0 : true)
    };
};

export const parseTileMatrixSetOption = (options) => {
    if (options?.availableTileMatrixSets) {
        const tileMatrixSetKeys = Object.keys(options.availableTileMatrixSets);
        const matrixIds = tileMatrixSetKeys.reduce((acc, key) => {
            const limits = options.availableTileMatrixSets[key].limits
                ? options.availableTileMatrixSets[key].limits
                : options.availableTileMatrixSets[key].tileMatrixSet.TileMatrix
                    .map((matrix) => ({ identifier: matrix['ows:Identifier'] }));
            return {
                ...acc,
                [key]: limits,
                [options.availableTileMatrixSets[key].crs]: limits
            };
        }, {});
        const tileMatrixSet = tileMatrixSetKeys
            .map((key) => options.availableTileMatrixSets[key].tileMatrixSet);
        return { ...options, matrixIds, tileMatrixSet };
    }
    return options;
};

/**
 * Parse layer options to get back the tile matrix sub-set that can be used.
 * This allows to have matrixIds and tileMatrixSet correctly sorted and filtered to match
 */
export const getTileMatrix = (_options, srs) => {
    const options = parseTileMatrixSetOption(_options);
    const tileMatrixSetName = getTileMatrixSet(options.tileMatrixSet, srs, options.allowedSRS, options.matrixIds);
    const ids = options.matrixIds && getMatrixIds(options.matrixIds, tileMatrixSetName || srs);
    const tileMatrixSet = sortTileMatrix(
        head(options.tileMatrixSet.filter(tM => tM['ows:Identifier'] === tileMatrixSetName)),
        ids);
    // identifiers are in the same order of scales and resolutions

    const identifiers = tileMatrixSet && tileMatrixSet.TileMatrix ? tileMatrixSet.TileMatrix.map(t => t["ows:Identifier"]) : [];
    // use same order of matrixIds in TileMatrix, if present.
    const matrixIds = identifiers && ids
        ? ids.sort((a, b) => {
            return identifiers.indexOf(a.identifier) - identifiers.indexOf(b.identifier);
        })
        : ids;
    return {
        matrixIds,
        tileMatrixSetName,
        tileMatrixSet: tileMatrixSet
    };
};

/**
 * Generate a WMTS url endpoint based on a WMS GeoServer layer configuration
 * @param {object} options layer options
 * @return {string} the WMTS url or an empty string
 */
export const generateGeoServerWMTSUrl = (options) => {
    const geoServerName = findGeoServerName(options);
    if (!geoServerName) {
        return '';
    }
    const baseUrl = getLayerUrl(options) || '';
    const parts = baseUrl.split(geoServerName);
    const layerParts = (options?.name || '').split(':');
    const workspacePath = layerParts.length === 2 ? `${layerParts[0]}/${layerParts[1]}/` : '';
    const wmtsCapabilitiesUrl = `${parts[0]}${geoServerName}${workspacePath}gwc/service/wmts?REQUEST=GetCapabilities`;
    return wmtsCapabilitiesUrl;
};

WMTSUtils = {
    getOperations,
    getOperation
};